import React from 'react';
import { Tooltip, Chip, Alert, Box, Typography } from '@mui/material';
import { Warning as WarningIcon, Info as InfoIcon } from '@mui/icons-material';

/**
 * Component to display fuzzy rate matching warnings
 */
const FuzzyMatchWarning = ({ 
  role, 
  variant = 'chip', 
  showDetails = false,
  sx = {} 
}) => {
  // Check if this role has fuzzy match information
  if (!role?.isFuzzyMatch || !role?.fuzzyMatchInfo) {
    return null;
  }

  const { originalRole, matchedRole, matchCount } = role.fuzzyMatchInfo;

  const warningMessage = `Role "${originalRole}" was mapped to "${matchedRole}" using fuzzy matching. Consider updating the role name or adding a specific rate for "${originalRole}".`;
  
  const detailsMessage = matchCount > 1 
    ? `Found ${matchCount} similar roles, using "${matchedRole}".`
    : `Using "${matchedRole}" as closest match.`;

  // Chip variant (compact, for inline display)
  if (variant === 'chip') {
    return (
      <Tooltip 
        title={
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Fuzzy Rate Mapping
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {warningMessage}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {detailsMessage}
            </Typography>
          </Box>
        }
        arrow
        placement="top"
      >
        <Chip
          icon={<WarningIcon />}
          label="Fuzzy Match"
          color="warning"
          size="small"
          variant="outlined"
          sx={{
            fontSize: '0.75rem',
            height: '24px',
            '& .MuiChip-icon': {
              fontSize: '16px'
            },
            ...sx
          }}
        />
      </Tooltip>
    );
  }

  // Alert variant (expanded, for detailed display)
  if (variant === 'alert') {
    return (
      <Alert 
        severity="warning" 
        icon={<WarningIcon />}
        sx={{ 
          mb: 1,
          '& .MuiAlert-message': {
            width: '100%'
          },
          ...sx 
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          Fuzzy Rate Mapping Detected
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Role <strong>"{originalRole}"</strong> was mapped to rate for <strong>"{matchedRole}"</strong>
        </Typography>
        {showDetails && (
          <Typography variant="body2" color="text.secondary">
            {detailsMessage} Consider adding a specific rate for "{originalRole}" or updating the role name to match exactly.
          </Typography>
        )}
      </Alert>
    );
  }

  // Icon variant (just the icon with tooltip)
  if (variant === 'icon') {
    return (
      <Tooltip title={warningMessage} arrow>
        <WarningIcon 
          color="warning" 
          fontSize="small"
          sx={{ 
            cursor: 'help',
            ml: 0.5,
            ...sx 
          }}
        />
      </Tooltip>
    );
  }

  // Inline variant (text with icon)
  if (variant === 'inline') {
    return (
      <Tooltip title={warningMessage} arrow>
        <Box 
          sx={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            color: 'warning.main',
            cursor: 'help',
            ...sx 
          }}
        >
          <WarningIcon fontSize="small" sx={{ mr: 0.5 }} />
          <Typography variant="caption" color="warning.main">
            Fuzzy Match: {originalRole} → {matchedRole}
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  return null;
};

export default FuzzyMatchWarning;